// The nationwide dots drawn straight on the GPU (R92, default since R95): a MapLibre custom layer fed by typed arrays from the map worker.
// No GeoJSON, no MapLibre tiling, no object copies: positions (static per catalog) and one state byte per site go to two buffers;
// a filter, hit or live change rewrites only the bytes of the sites that changed.
// State byte: 0..15 availability tone, 16 out of service, 17 unknown, HIDDEN = not drawn (filtered out or a ranked hit pin).
import {bubbleStyle} from './bubbles.mjs';
import {dotRadius,PIN_ZOOM} from './map-geometry.mjs';
import {dotOpacityAt, FOCUS_DIM} from './dot-variants.mjs';

export const HIDDEN = 255, TONES = 18;
export const toneCode = tone => tone === 'out' ? 16 : tone === 'unknown' ? 17 : Number(tone);
const GRID = 4096;
const hexToRgb = hex => { const v = parseInt(hex.slice(1, 7), 16); return [(v >> 16) / 255, ((v >> 8) & 255) / 255, (v & 255) / 255]; };
// Same curves as the circle layer: radius and opacity by zoom come from map-geometry.mjs / dot-variants.mjs.
export const gpuRadius = zoom => dotRadius(zoom);
export const gpuOpacity = (zoom, focused) => dotOpacityAt(zoom) * (focused ? FOCUS_DIM : 1);
const mercatorLon = x => x * 360 - 180, mercatorLat = y => Math.atan(Math.sinh(Math.PI * (1 - 2 * y))) * 180 / Math.PI;
const mercatorX = lon => (lon + 180) / 360, mercatorY = lat => .5 - Math.log(Math.tan(Math.PI / 4 + Math.max(-85.05, Math.min(85.05, lat)) * Math.PI / 360)) / (2 * Math.PI);

const VERTEX = (shaderData) => `#version 300 es
precision highp float;
${shaderData.define}
${shaderData.vertexShaderPrelude}
in vec2 a_pos;
in float a_state;
uniform vec3 u_colors[${TONES}];
uniform float u_radius;
uniform float u_pixel;
uniform float u_opacity;
out vec3 v_color;
out float v_alpha;
out float v_radius;
void main() {
  if (a_state > 200.0) { gl_Position = vec4(2.0, 2.0, 2.0, 1.0); gl_PointSize = 0.0; v_color = vec3(0.0); v_alpha = 0.0; v_radius = 0.0; return; }
  gl_Position = projectTile(a_pos);
  v_color = u_colors[int(a_state + 0.5)];
  v_alpha = u_opacity;
  v_radius = u_radius * u_pixel;
  gl_PointSize = 2.0 * v_radius + 2.0;
}`;
const FRAGMENT = `#version 300 es
precision mediump float;
in vec3 v_color;
in float v_alpha;
in float v_radius;
out vec4 o_color;
void main() {
  float d = length(gl_PointCoord - 0.5) * (2.0 * v_radius + 2.0);
  // Like MapLibre's circle layer: full coverage one device pixel inside the radius, none at the radius.
  float a = clamp(v_radius - d, 0.0, 1.0) * v_alpha;
  o_color = vec4(v_color * a, a);
}`;

export class GpuDots {
  constructor() {
    this.id = 'site-dots'; this.type = 'custom'; this.renderingMode = '2d';
    this.n = 0; this.pos = new Float32Array(0); this.state = new Uint8Array(0); this.ids = '';
    this.theme = 'light'; this.profile = {}; this.focused = false; this.visible = true; this.failed = false; this.onFail = null;
    this.dirtyAll = true; this.dirty = []; this.gl = null; this.program = null; this.grid = null; this.idList = null;
    this.colors = new Float32Array(TONES * 3); this.palette();
    this.frames = 0;
  }
  // Colours come from the same quantised, dimmed fills as the pins and the circle layer.
  palette() {
    for (let i = 0; i < TONES; i++) this.colors.set(hexToRgb(bubbleStyle(i === 16 ? 'out' : i === 17 ? 'unknown' : String(i), this.theme, this.profile).fill), i * 3);
  }
  configure({theme, profile, focused}) { this.theme = theme; this.profile = profile; this.focused = !!focused; this.palette(); this.repaint(); }
  setTheme(theme, profile) { this.theme = theme; this.profile = profile; this.palette(); this.repaint(); }
  setFocus(focused) { this.focused = !!focused; this.repaint(); }
  repaint() { this.map?.triggerRepaint(); }

  // Worker results: {n, pos?, ids?, state} replaces everything (positions only when the catalog changed), {idx, state} patches sites.
  apply(d) {
    if (d.state && !d.idx) {
      if (d.pos) { this.pos = d.pos; this.grid = null; this.posDirty = true; }
      if (d.ids !== undefined) { this.ids = d.ids; this.idList = null; }
      this.n = d.n; this.state = d.state; this.dirtyAll = true; this.dirty = [];
    } else if (d.idx) {
      for (let k = 0; k < d.idx.length; k++) { this.state[d.idx[k]] = d.state[k]; if (!this.dirtyAll) this.dirty.push(d.idx[k]); }
    }
    this.repaint();
  }
  get ready() { return this.n > 0; }

  onAdd(map, gl) { this.map = map; this.init(gl); }
  onRemove(map, gl) { if (!gl.isContextLost()) this.free(gl); this.program = null; this.map = null; }
  free(gl) { gl.deleteProgram(this.program); gl.deleteBuffer(this.posBuffer); gl.deleteBuffer(this.stateBuffer); gl.deleteVertexArray(this.vao); }
  init(gl, shaderData) {
    this.gl = gl; this.program = null; this.dirtyAll = true; this.posUploaded = false; this.dirty = [];
    if (!shaderData) return; // compiled on the first render: the shader prelude of the current projection arrives there
    const compile = (type, source) => { const s = gl.createShader(type); gl.shaderSource(s, source); gl.compileShader(s); if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) throw Error(gl.getShaderInfoLog(s)); return s; };
    const program = gl.createProgram();
    gl.attachShader(program, compile(gl.VERTEX_SHADER, VERTEX(shaderData))); gl.attachShader(program, compile(gl.FRAGMENT_SHADER, FRAGMENT));
    gl.bindAttribLocation(program, 0, 'a_pos'); gl.bindAttribLocation(program, 1, 'a_state');
    gl.linkProgram(program);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) throw Error(gl.getProgramInfoLog(program));
    this.program = program; this.variantName = shaderData.variantName;
    // Points larger than the device allows would be cut square; the radius is capped below that size.
    this.maxPoint = gl.getParameter?.(gl.ALIASED_POINT_SIZE_RANGE)?.[1] || 1024;
    this.uniforms = Object.fromEntries(['u_colors', 'u_radius', 'u_pixel', 'u_opacity', 'u_projection_matrix', 'u_projection_tile_mercator_coords', 'u_projection_clipping_plane', 'u_projection_transition', 'u_projection_fallback_matrix']
      .map(name => [name, gl.getUniformLocation(program, name)]));
    this.vao = gl.createVertexArray(); this.posBuffer = gl.createBuffer(); this.stateBuffer = gl.createBuffer();
    gl.bindVertexArray(this.vao);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.posBuffer); gl.enableVertexAttribArray(0); gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.stateBuffer); gl.enableVertexAttribArray(1); gl.vertexAttribPointer(1, 1, gl.UNSIGNED_BYTE, false, 0, 0);
    gl.bindVertexArray(null);
  }
  upload(gl) {
    if (this.posDirty || !this.posUploaded) { gl.bindBuffer(gl.ARRAY_BUFFER, this.posBuffer); gl.bufferData(gl.ARRAY_BUFFER, this.pos, gl.STATIC_DRAW); this.posDirty = false; this.posUploaded = true; }
    if (this.dirtyAll) { gl.bindBuffer(gl.ARRAY_BUFFER, this.stateBuffer); gl.bufferData(gl.ARRAY_BUFFER, this.state, gl.DYNAMIC_DRAW); this.dirtyAll = false; this.dirty = []; }
    else if (this.dirty.length) {
      gl.bindBuffer(gl.ARRAY_BUFFER, this.stateBuffer);
      if (this.dirty.length > 512) gl.bufferSubData(gl.ARRAY_BUFFER, 0, this.state);
      else for (const i of this.dirty) gl.bufferSubData(gl.ARRAY_BUFFER, i, this.state, i, 1);
      this.dirty = [];
    }
  }
  render(gl, args) {
    if (!this.visible || !this.n || this.failed) return;
    const zoom = this.map.getZoom();
    if (zoom >= PIN_ZOOM) return;
    if (!this.program || this.gl !== gl || this.variantName !== args.shaderData.variantName) {
      try { if (this.program && !gl.isContextLost()) this.free(gl); this.init(gl, args.shaderData); }
      // No WebGL2 or a shader the driver rejects: stop drawing and let the page fall back to the circle layer.
      catch (error) { this.failed = true; this.program = null; console.error('GPU dots unavailable', error); this.onFail?.(error); return; }
    }
    this.upload(gl);
    const p = args.defaultProjectionData, u = this.uniforms;
    gl.useProgram(this.program);
    gl.uniform3fv(u.u_colors, this.colors);
    const pixel = this.map.getPixelRatio?.() ?? globalThis.devicePixelRatio ?? 1;
    gl.uniform1f(u.u_radius, Math.min(gpuRadius(zoom), (this.maxPoint - 2) / 2 / pixel));
    gl.uniform1f(u.u_pixel, pixel);
    gl.uniform1f(u.u_opacity, gpuOpacity(zoom, this.focused));
    gl.uniformMatrix4fv(u.u_projection_matrix, false, p.mainMatrix);
    if (u.u_projection_tile_mercator_coords) gl.uniform4fv(u.u_projection_tile_mercator_coords, p.tileMercatorCoords);
    if (u.u_projection_clipping_plane) gl.uniform4fv(u.u_projection_clipping_plane, p.clippingPlane);
    if (u.u_projection_transition) gl.uniform1f(u.u_projection_transition, p.projectionTransition);
    if (u.u_projection_fallback_matrix) gl.uniformMatrix4fv(u.u_projection_fallback_matrix, false, p.fallbackMatrix);
    gl.enable(gl.BLEND); gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
    gl.bindVertexArray(this.vao);
    gl.drawArrays(gl.POINTS, 0, this.n);
    gl.bindVertexArray(null);
    this.frames++;
  }

  // Picking on the CPU: candidates around a screen point, as features like the circle layer returned them.
  hits(map, point, box = 24) {
    if (!this.n || !this.visible || map.getZoom() >= PIN_ZOOM) return [];
    const corners = [[-box, -box], [box, -box], [box, box], [-box, box]].map(([dx, dy]) => map.unproject([point.x + dx, point.y + dy]));
    if (corners.some(c => !c || !Number.isFinite(c.lng) || !Number.isFinite(c.lat))) return [];
    const lons = corners.map(c => c.lng), wide = Math.max(...lons) - Math.min(...lons) > 180;
    const x0 = mercatorX(Math.min(...lons)), x1 = mercatorX(Math.max(...lons)), ys = corners.map(c => mercatorY(c.lat)), y0 = Math.min(...ys), y1 = Math.max(...ys);
    const candidates = [], pos = this.pos;
    if (wide || (x1 - x0) * GRID * (y1 - y0) * GRID > 20000) { for (let i = 0; i < this.n; i++) if (this.state[i] !== HIDDEN) candidates.push(i); }
    else {
      const grid = this.cells();
      for (let cx = Math.floor(x0 * GRID); cx <= Math.floor(x1 * GRID); cx++) for (let cy = Math.floor(y0 * GRID); cy <= Math.floor(y1 * GRID); cy++)
        for (const i of grid.get(cx * GRID + cy) || []) if (this.state[i] !== HIDDEN) candidates.push(i);
    }
    const ids = this.idArray(), out = [];
    for (const i of candidates) {
      const coordinates = [mercatorLon(pos[2 * i]), mercatorLat(pos[2 * i + 1])];
      out.push({layer: {id: this.id}, properties: {id: ids[i]}, geometry: {type: 'Point', coordinates}});
    }
    return out;
  }
  cells() {
    if (this.grid) return this.grid;
    const grid = new Map();
    for (let i = 0; i < this.n; i++) { const key = Math.floor(this.pos[2 * i] * GRID) * GRID + Math.floor(this.pos[2 * i + 1] * GRID); const cell = grid.get(key); if (cell) cell.push(i); else grid.set(key, [i]); }
    return this.grid = grid;
  }
  idArray() { return this.idList ??= this.ids.split(','); }
}
export const mercatorPosition = (lon, lat) => [mercatorX(lon), mercatorY(lat)];
